import { useSearchLaptop } from "api/Client/product";
import { image_host } from "config/api_host";
import { motion } from "framer-motion";
import debounce from "lodash.debounce";
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Hash,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { Link, useParams } from "react-router-dom";

import LogoSpinner from "components/Spinner/LogoSpinner";

function Searchs() {
  // --- States based on your requirements ---
  // البحث العام
  const [searchInput, setSearchInput] = useState(""); // قيمة الحقل
  const [searchTerm, setSearchTerm] = useState(""); // القيمة المُدبَّنسة
  const [company, setCompany] = useState("");
  const [selectedCpus, setSelectedCpus] = useState([]); // Matrix for checkboxes
  const [ram_from, setRamFrom] = useState("");
  const [ram_to, setRamTo] = useState("");
  const [price_from, setPriceFrom] = useState("");
  const [price_to, setPriceTo] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const resultsSectionRef = useRef(null);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchTerm(value);
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // إعادة الصفحة للأولى عند تغيير أي فلتر
  useEffect(() => {
    setPageNumber(1);
  }, [
    searchTerm,
    company,
    selectedCpus,
    ram_from,
    ram_to,
    price_from,
    price_to,
  ]);

  // التجهيز لأول الـ section عند تغيير رقم الصفحة
  useEffect(() => {
    if (resultsSectionRef.current) {
      resultsSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [pageNumber]);

  const handleCpuChange = (cpuValue) => {
    if (selectedCpus.includes(cpuValue)) {
      setSelectedCpus(selectedCpus.filter((item) => item !== cpuValue));
    } else {
      setSelectedCpus([...selectedCpus, cpuValue]);
    }
  };

  const cpuOptions = [
    // Intel
    { id: "intel", label: "Intel" },
    { id: "amd", label: "AMD" },
    { id: "celeron", label: "Intel Celeron" },
    { id: "i3", label: "Intel Core i3" },
    { id: "i5", label: "Intel Core i5" },
    { id: "i7", label: "Intel Core i7" },
    { id: "i9", label: "Intel Core i9" },
    { id: "xeon", label: "Intel Xeon" },

    // AMD
    { id: "ryzen3", label: "Ryzen 3" },
    { id: "ryzen5", label: "Ryzen 5" },
    { id: "ryzen7", label: "Ryzen 7" },
    { id: "ryzen9", label: "Ryzen 9" },
  ];

  const {
    data: product,
    isLoading: productIsLoading,
    isSuccess: productIsSuccess,
  } = useSearchLaptop(
    searchTerm,
    price_from,
    price_to,
    selectedCpus,
    ram_from,
    ram_to,
    company,
    pageNumber
  );

  // الحالة الأولى: لا يوجد أي منهما
  return (
    <section className="py-24_ px-4 mt-8">
      <div className="container mx-auto text-center max-w-6xl">
        <h2 className="text-3xl font-bold mb-12 text-mainLight">
          ابحث عن لابتوب أحلامك
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 border-b pb-8 border-gray-300/30 dark:border-gray-600">
          {/* Column 1: Search & Company */}
          <div className="space-y-6">
            <div
              className={`
  p-6 rounded-2xl border shadow-sm transition-all duration-500
  bg-gray-50  hover:border-gray-300
border-gray-200 dark:border-white/10  
  dark:bg-[#151a28] dark:hover:border-gray-700
`}
            >
              <div className="flex items-center gap-2 mb-4 text-yellow-500">
                <Hash size={18} />
                <h3 className="font-bold">البحث العام</h3>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="اسم الجهاز أو الموديل..."
                  value={searchInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchInput(value);
                    debouncedSearch(value);
                  }}
                  className={`
    w-full px-4 py-2.5 rounded-xl border outline-none transition-all duration-300
    bg-white border-gray-300 text-gray-800
    focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10

    dark:bg-[#1a1f2e] dark:border-gray-700 dark:text-gray-200
    dark:focus:border-yellow-500/50 dark:focus:ring-yellow-500/10
  pr-10
  `}
                />

                <Search
                  className="absolute left-3 top-3 text-gray-400"
                  size={18}
                />
              </div>
            </div>

            <div
              className={`
  p-6 rounded-2xl border shadow-sm transition-all duration-500
  bg-gray-50  hover:border-gray-300
border-gray-200 dark:border-white/10  
  dark:bg-[#151a28] dark:hover:border-gray-700
`}
            >
              <div className="flex items-center gap-2 mb-4 text-blue-500">
                <Building2 size={18} />
                <h3 className="font-bold">الماركة</h3>
              </div>
              <select
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className={`
    w-full px-4 py-2.5 rounded-xl border outline-none transition-all duration-300
    bg-white border-gray-300 text-gray-800
    focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10

    dark:bg-[#1a1f2e] dark:border-gray-700 dark:text-gray-200
    dark:focus:border-yellow-500/50 dark:focus:ring-yellow-500/10
  `}
              >
                <option value="">جميع الشركات</option>
                <option value="apple">Apple</option>
                <option value="dell">Dell</option>
                <option value="hp">HP</option>
                <option value="lenovo">Lenovo</option>
                <option value="asus">Asus</option>
                <option value="acer">Acer</option>
                <option value="msi">MSI</option>
                <option value="razer">Razer</option>
                <option value="samsung">Samsung</option>
                <option value="lg">LG</option>
                <option value="huawei">Huawei</option>
                <option value="toshiba">Toshiba</option>
                <option value="vaio">VAIO</option>
                <option value="alienware">Alienware</option>
              </select>
            </div>
          </div>

          {/* Column 2: CPU Checkboxes */}
          <div
            className={`
  p-6 rounded-2xl border shadow-sm transition-all duration-500
  bg-gray-50  hover:border-gray-300
border-gray-200 dark:border-white/10  
  dark:bg-[#151a28] dark:hover:border-gray-700
`}
          >
            <div className="flex items-center gap-2 mb-4 text-emerald-500">
              <Cpu size={20} />
              <h3 className="font-bold">نوع المعالج</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {cpuOptions.map((opt) => (
                <label
                  key={opt.id}
                  className="
  flex items-center p-1.5 rounded-xl border cursor-pointer transition-all

  bg-white border-gray-200 text-gray-600 hover:border-emerald-300
  dark:bg-[#1a1f2e] dark:border-gray-700 dark:text-gray-400
 
  has-[:checked]:bg-emerald-50
  has-[:checked]:border-emerald-500
  has-[:checked]:text-emerald-700

  dark:has-[:checked]:bg-emerald-500/10
  dark:has-[:checked]:border-emerald-500/50
  dark:has-[:checked]:text-emerald-400
"
                >
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={selectedCpus.includes(opt.id)}
                    onChange={() => handleCpuChange(opt.id)}
                  />
                  <span className="text-sm font-medium text-center w-full mx-auto">
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Column 3: RAM & Price Range */}
          <div className="space-y-6">
            <div
              className={`
  p-6 rounded-2xl border shadow-sm transition-all duration-500 border-gray-200 dark:border-white/10 
  bg-gray-50 hover:border-gray-300 dark:bg-[#151a28] dark:hover:border-gray-700
`}
            >
              <div className="flex items-center gap-2 mb-4 text-purple-500">
                <SlidersHorizontal size={18} />
                <h3 className="font-bold">الذاكرة (RAM)</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  placeholder="من GB"
                  value={ram_from}
                  onChange={(e) => setRamFrom(e.target.value)}
                  className={`
  w-full px-4 py-2.5 rounded-xl border outline-none transition-all duration-300
  bg-white border-gray-300 text-gray-800
  focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10
  dark:bg-[#1a1f2e] dark:border-gray-700 dark:text-gray-200
  dark:focus:border-yellow-500/50 dark:focus:ring-yellow-500/10
`}
                />
                <input
                  type="number"
                  placeholder="إلى GB"
                  value={ram_to}
                  onChange={(e) => setRamTo(e.target.value)}
                  className={`
  w-full px-4 py-2.5 rounded-xl border outline-none transition-all duration-300
  bg-white border-gray-300 text-gray-800 
  focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10
  dark:bg-[#1a1f2e] dark:border-gray-700 dark:text-gray-200
  dark:focus:border-yellow-500/50 dark:focus:ring-yellow-500/10
`}
                />
              </div>
            </div>

            <div
              className={`
  p-6 rounded-2xl border shadow-sm transition-all duration-500
  bg-gray-50 hover:border-gray-300
border-gray-200 dark:border-white/10  
  dark:bg-[#151a28] dark:hover:border-gray-700
`}
            >
              <div
                ref={resultsSectionRef}
                className="flex items-center gap-2 mb-4 text-amber-500"
              >
                <span className="text-lg font-bold">$</span>
                <h3 className="font-bold">نطاق السعر</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  placeholder="من $"
                  value={price_from}
                  onChange={(e) => setPriceFrom(e.target.value)}
                  className={`
  w-full px-4 py-2.5 rounded-xl border outline-none transition-all duration-300
  bg-white border-gray-300 text-gray-800
  focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10

  dark:bg-[#1a1f2e] dark:border-gray-700 dark:text-gray-200
  dark:focus:border-yellow-500/50 dark:focus:ring-yellow-500/10
`}
                />
                <input
                  type="number"
                  placeholder="إلى $"
                  value={price_to}
                  onChange={(e) => setPriceTo(e.target.value)}
                  className={`
  w-full px-4 py-2.5 rounded-xl border outline-none transition-all duration-300
  bg-white border-gray-300 text-gray-800
  focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10

  dark:bg-[#1a1f2e] dark:border-gray-700 dark:text-gray-200
  dark:focus:border-yellow-500/50 dark:focus:ring-yellow-500/10
`}
                />
              </div>
            </div>
          </div>
        </div>

        {productIsLoading && <LogoSpinner page />}
        {productIsSuccess && product.data?.laptops?.length > 0 && (
          <>
            <div className="scroll-mt-6">
              <section className="p-4 py-6 font-[Tajawal] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto w-full">
                {product.data.laptops.map((lap) => (
                  <LaptopCard key={lap.id} laptop={lap} />
                ))}
              </section>
              {product.data.pagination &&
                product.data.pagination.last_page > 1 && (
                  <Pagination
                    currentPage={product.data.pagination.current_page}
                    lastPage={product.data.pagination.last_page}
                    total={product.data.pagination.total}
                    perPage={product.data.pagination.per_page}
                    onPageChange={setPageNumber}
                  />
                )}
            </div>
          </>
        )}
        {productIsSuccess &&
          (!product.data?.laptops || product.data.laptops.length === 0) && (
            <div className="py-24 text-center max-w-xl mx-auto">
              <div className="p-8 rounded-3xl border-2 border-dashed border-mainLight/30 bg-gray-50 dark:bg-white/5 shadow-xl">
                <div className="text-6xl mb-6 opacity-80">🔍</div>
                <h3 className="text-xl font-bold text-gray-700 dark:text-gray-200 mb-2">
                  لا توجد منتجات مطابقة
                </h3>
                <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                  لم نجد لابتوبات تطابق بحثك حالياً. سنعمل على توفيرها قريباً.
                </p>
              </div>
            </div>
          )}
      </div>
    </section>
  );
}

function LaptopCard({ laptop }) {
  const [flipped, setFlipped] = useState(false);

  const technicalFields = laptop.fields ? laptop.fields : [];
  const isAvailable = laptop.quantity > 0 || laptop.isAvailable === 1;
  const hasDiscount =
    Number(laptop.discountPriceDollar) > 0 &&
    Number(laptop.discountPriceDollar) < Number(laptop.salesPriceDollar);

  return (
    <div
      className="relative w-full h-[450px] [perspective:1500px] group cursor-pointer"
      onClick={() => setFlipped(!flipped)}
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.7, type: "spring", stiffness: 100 }}
        className="relative w-full h-full [transform-style:preserve-3d]"
      >
        {/* --- الوجه الأمامي: العرض التسويقي --- */}
        <div className="absolute inset-0 rounded-[2rem] border border-gray-200 dark:border-white/10  p-4 bg-gray-100 dark:bg-white/5 backdrop-blur-md [backface-visibility:hidden] flex flex-col items-center shadow-xl dark:shadow-2xl transition-colors duration-300">
          {/* شارة التوفر */}
          <div
            className={`absolute top-4 right-4 px-3 py-1 rounded-full text-[10px] font-bold z-10 ${
              isAvailable
                ? "bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400"
                : "bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400"
            }`}
          >
            {isAvailable ? "متوفر في المخزن" : "نفدت الكمية"}
          </div>

          {/* شارة الخصم */}
          {hasDiscount && (
            <div className="absolute top-4 left-4 bg-red-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg z-20 animate-bounce">
              خصم{" "}
              {Math.round(
                ((laptop.salesPriceDollar - laptop.discountPriceDollar) /
                  laptop.salesPriceDollar) *
                  100
              )}
              %
            </div>
          )}

          {/* صورة المنتج */}
          <div className="mt-8 relative h-36 flex items-center">
            <div className="absolute inset-0 bg-mainLight/20 dark:bg-mainLight/10 blur-3xl rounded-full opacity-50 dark:opacity-100"></div>
            <img
              src={image_host + laptop.image}
              alt={laptop.name}
              className="relative z-10 w-48 h-full object-contain drop-shadow-xl dark:drop-shadow-[0_15px_30px_rgba(0,0,0,0.4)]"
            />
          </div>

          {/* النصوص */}
          <div className="text-center mt-4 w-full px-2">
            <span className="text-mainLight text-xs font-bold tracking-widest uppercase opacity-80">
              {laptop.companyName}
            </span>
            <h3
              dir="ltr"
              className="text-2xl font-bold text-gray-800 dark:text-white mt-1 truncate"
            >
              {laptop.name}
            </h3>
            {laptop.desc && (
              <p className="text-gray-500 dark:text-gray-400 text-xs mt-2 line-clamp-3 leading-relaxed">
                {laptop.desc}
              </p>
            )}
          </div>

          {/* السعر */}
          <div className="mt-auto w-full flex justify-center items-center gap-3 bg-gray-50 dark:bg-white/5 py-3 rounded-2xl border border-gray-100 dark:border-white/5 transition-colors">
            {hasDiscount ? (
              <>
                <s className="text-gray-400 dark:text-gray-500 font-bold text-sm">
                  ${Number(laptop.salesPriceDollar).toLocaleString()}
                </s>
                <span className="text-mainLight font-bold text-xl">
                  ${Number(laptop.discountPriceDollar).toLocaleString()}
                </span>
              </>
            ) : (
              <span className="text-mainLight font-bold text-xl">
                ${Number(laptop.salesPriceDollar).toLocaleString()}
              </span>
            )}
          </div>
        </div>

        {/* --- الوجه الخلفي: المعلومات الكاملة --- */}
        <div className="absolute inset-0 rounded-[2rem] border border-mainLight/30 p-4 bg-gray-200 dark:bg-mainDark/95 backdrop-blur-2xl [transform:rotateY(180deg)] [backface-visibility:hidden] flex flex-col shadow-2xl transition-colors duration-300">
          <div className="flex justify-between items-start mb-4">
            <div className="text-right">
              <h4 className="text-gray-800 dark:text-white font-bold text-lg">
                {laptop.name}
              </h4>
              <p className="text-mainLight text-xs font-bold uppercase">
                Technical Specs
              </p>
            </div>
            <div className="bg-mainLight/10 dark:bg-mainLight/20 p-2 rounded-lg">
              <FaInfoCircle className="text-mainLight" />
            </div>
          </div>

          {/* المواصفات */}
          <div className="grid grid-cols-2 gap-3 flex-1 overflow-y-auto pt-1 pb-2 custom-scrollbar">
            {technicalFields.map((field, idx) => (
              <div
                key={idx}
                className="bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-1.5 h-12 rounded-xl flex flex-col items-center justify-center text-center transition-colors"
              >
                <span className="text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase">
                  {field.name}
                </span>
                <span className="text-gray-700 dark:text-white text-xs font-bold truncate w-full px-1">
                  {field.value}
                </span>
              </div>
            ))}
          </div>

          {/* الكمية */}
          <div className="bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-2 rounded-xl h-12 flex flex-col items-center justify-center text-center mb-4 transition-colors">
            <span className="text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase">
              الكمية المتاحة
            </span>
            <span
              className={`text-xs font-bold ${
                laptop.quantity < 5
                  ? "text-orange-500"
                  : "text-gray-700 dark:text-white"
              }`}
            >
              {laptop.quantity} قطعة
            </span>
          </div>

          {/* زر الشراء */}
          <div className="mt-auto">
            <Link
              to={`/product/${laptop.id}`}
              onClick={(e) => e.stopPropagation()}
              className="block w-full py-3 rounded-xl bg-mainLight text-mainDark font-bold text-center text-sm shadow-lg hover:scale-[1.02] active:scale-95 transition-all"
            >
              المزيد من التفاصيل
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Tags({ data }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-sm">
      {data.map((t) => (
        <Tag label={t.name} value={t.value || " - - "} />
      ))}
    </div>
  );
}

function Tag({ label, value }) {
  return (
    <div className="flex flex-col border border-gray-300/30 dark:border-gray-600 rounded-lg p-2 text-center">
      <span className="text-[11px] uppercase text-gray-400">{label}</span>
      <span className="font-medium text-sm truncate">{value}</span>
    </div>
  );
}

function LaptopPrand({ prand }) {
  return (
    <motion.div whileHover={{ y: -10 }} className="relative group h-fit">
      <Link to={`${prand.id}`} className="flex flex-col items-center gap-4">
        <div className="relative w-full aspect-square flex items-center justify-center p-8 rounded-[2.5rem] bg-white/5 border border-white/10 backdrop-blur-xl shadow-2xl_ group-hover:border-mainLight/40 group-hover:shadow-mainLight/10 transition-all duration-500">
          {/* تأثير الضوء خلف الشعار */}
          <div className="absolute inset-0 bg-mainLight/5 opacity-0 group-hover:opacity-100 rounded-[2.5rem] blur-2xl transition-opacity"></div>

          <img
            src={image_host + prand.image}
            alt={prand.name}
            className="w-full h-full object-contain filter   transition-all duration-500 drop-shadow-lg"
          />
        </div>
        <h3 className="text-xl font-bold text-gray-300 group-hover:text-mainLight transition-colors">
          {prand.name}
        </h3>
      </Link>
    </motion.div>
  );
}

function Pagination({ currentPage, lastPage, total, perPage, onPageChange }) {
  const from = (currentPage - 1) * perPage + 1;
  const to = Math.min(currentPage * perPage, total);

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;
    for (let i = 1; i <= lastPage; i++) {
      if (
        i === 1 ||
        i === lastPage ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        range.push(i);
      }
    }
    for (const i of range) {
      if (l !== undefined && i - l !== 1) rangeWithDots.push(-1);
      rangeWithDots.push(i);
      l = i;
    }
    return rangeWithDots;
  };

  const pages = getVisiblePages();

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-8 px-4 max-w-6xl mx-auto w-full border-t border-gray-200 dark:border-white/10 pt-8">
      <p className="text-sm text-gray-500 dark:text-gray-400 order-2 sm:order-1">
        عرض {from}–{to} من {total}
      </p>
      <nav
        className="flex items-center gap-1 order-1 sm:order-2"
        aria-label="ترقيم الصفحات"
      >
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="p-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:border-mainLight/40 hover:bg-mainLight/10 hover:text-mainLight disabled:opacity-40 disabled:pointer-events-none transition-all"
          aria-label="الصفحة السابقة"
        >
          <ChevronRight size={20} />
        </button>
        <div className="flex items-center gap-1 mx-2">
          {pages.map((p, i) =>
            p === -1 ? (
              <span
                key={`dot-${i}`}
                className="px-2 text-gray-400 dark:text-gray-500"
              >
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                className={`min-w-[2.5rem] h-10 px-3 rounded-xl border font-bold text-sm transition-all ${
                  p === currentPage
                    ? "border-mainLight/50 bg-mainLight/20 text-mainLight"
                    : "border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-300 hover:border-mainLight/40 hover:bg-mainLight/10 hover:text-mainLight"
                }`}
              >
                {p}
              </button>
            )
          )}
        </div>
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= lastPage}
          className="p-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:border-mainLight/40 hover:bg-mainLight/10 hover:text-mainLight disabled:opacity-40 disabled:pointer-events-none transition-all"
          aria-label="الصفحة التالية"
        >
          <ChevronLeft size={20} />
        </button>
      </nav>
    </div>
  );
}

export default Searchs;
